import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  MessageSquare, Plus, Send, Paperclip, ArrowLeft, Loader2,
  HelpCircle, FileText, Image, Download, X, CheckCircle2,
  Headphones, User, Clock, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { formatLabel } from '@/lib/format-labels';

const ONBOARDING_TOPICS = [
  { value: 'onboarding_query', label: 'Application Query' },
  { value: 'document_help', label: 'Document Help' },
  { value: 'verification_issue', label: 'Verification Issue' },
  { value: 'general', label: 'General Question' },
];

type PanelView = 'list' | 'new' | 'thread';

interface OnboardingSupportPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OnboardingSupportPanel({ open, onOpenChange }: OnboardingSupportPanelProps) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<PanelView>('list');
  const [userId, setUserId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);

  // New ticket form
  const [newTopic, setNewTopic] = useState('onboarding_query');
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [creating, setCreating] = useState(false);

  // Thread state
  const [threadMessage, setThreadMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Resolve user + client IDs
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setUserId(data.user.id);
    });
  }, []);

  useEffect(() => {
    if (!userId) return;
    // Look up client_profiles by user_id (RLS allows own profile)
    supabase.from('client_profiles').select('id').eq('user_id', userId).maybeSingle()
      .then(({ data }) => {
        if (data?.id) { setClientId(data.id); return; }
        // No profile yet — fall back: get email from auth, find onboarding session
        supabase.auth.getUser().then(({ data: authData }) => {
          const email = authData.user?.email;
          if (!email) return;
          (supabase as any).from('onboarding_sessions').select('id')
            .eq('email', email.toLowerCase())
            .order('created_at', { ascending: false })
            .limit(1).maybeSingle()
            .then(({ data: s }: { data: any }) => {
              // Store session id — note: ticket creation will need a real client_profiles.id
              if (s?.id) setClientId(s.id);
            });
        });
      });
  }, [userId]);

  // Fetch tickets
  const { data: tickets = [], isLoading: ticketsLoading } = useQuery({
    queryKey: ['onboarding-support-tickets', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('client_id', clientId)
        .order('last_message_at', { ascending: false });
      return data || [];
    },
    enabled: !!clientId,
    refetchInterval: 15000,
  });

  // Fetch messages for active ticket
  const { data: messages = [], isLoading: messagesLoading } = useQuery({
    queryKey: ['onboarding-support-messages', activeTicketId],
    queryFn: async () => {
      if (!activeTicketId) return [];
      const { data } = await supabase
        .from('support_messages')
        .select('*')
        .eq('ticket_id', activeTicketId)
        .order('created_at', { ascending: true });
      return data || [];
    },
    enabled: !!activeTicketId,
    refetchInterval: 10000,
  });

  // Realtime for new messages
  useEffect(() => {
    if (!activeTicketId) return;
    const channel = supabase
      .channel(`onb-support-${activeTicketId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'support_messages',
        filter: `ticket_id=eq.${activeTicketId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['onboarding-support-messages', activeTicketId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeTicketId, queryClient]);

  // Realtime for ticket updates
  useEffect(() => {
    if (!clientId) return;
    const channel = supabase
      .channel(`onb-tickets-${clientId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'support_tickets',
        filter: `client_id=eq.${clientId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['onboarding-support-tickets', clientId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [clientId, queryClient]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (view === 'thread') {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages.length, view]);

  const unreadCount = tickets.filter((t: any) =>
    t.status === 'awaiting_client' || (t.last_message_at && t.status !== 'resolved' && t.status !== 'closed')
  ).length;

  const handleCreateTicket = async () => {
    if (!clientId || !newSubject.trim() || !newMessage.trim()) return;
    setCreating(true);
    try {
      // Get session ID for linking
      let sessionId: string | null = null;
      const { data: authData } = await supabase.auth.getUser();
      const email = authData.user?.email;
      if (email) {
        const { data: session } = await (supabase as any)
          .from('onboarding_sessions')
          .select('id')
          .eq('email', email.toLowerCase())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        sessionId = session?.id || null;
      }

      const { data: ticket, error } = await supabase
        .from('support_tickets')
        .insert({
          client_id: clientId,
          topic: newTopic as any,
          subject: newSubject.trim(),
          session_id: sessionId,
          metadata: { source: 'onboarding' },
        })
        .select('id')
        .single();

      if (error) throw error;

      // Send the initial message
      await supabase.from('support_messages').insert({
        ticket_id: ticket.id,
        sender_type: 'client',
        sender_id: userId,
        sender_name: 'Applicant',
        content: newMessage.trim(),
      });

      // Notify admin
      await supabase.functions.invoke('notify-admin', {
        body: {
          recipient_user_id: '00000000-0000-0000-0000-000000000000',
          title: `New Support Ticket — ${newSubject.trim()}`,
          body: newMessage.trim().slice(0, 200),
          type: 'message',
          link: '/admin/support-tickets',
          scope: 'team',
        },
      }).catch(() => {});

      queryClient.invalidateQueries({ queryKey: ['onboarding-support-tickets', clientId] });
      setNewSubject('');
      setNewMessage('');
      setNewTopic('onboarding_query');
      setActiveTicketId(ticket.id);
      setView('thread');
      toast.success('Ticket created');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!activeTicketId || (!threadMessage.trim() && files.length === 0)) return;
    setSending(true);
    try {
      let attachments: any[] = [];
      for (const file of files) {
        const path = `${clientId || 'anon'}/${activeTicketId}/${Date.now()}-${file.name}`;
        const { error } = await supabase.storage.from('support-attachments').upload(path, file);
        if (!error) {
          attachments.push({
            file_name: file.name,
            file_path: path,
            mime_type: file.type,
            size: file.size,
          });
        }
      }

      await supabase.from('support_messages').insert({
        ticket_id: activeTicketId,
        sender_type: 'client',
        sender_id: userId,
        sender_name: 'Applicant',
        content: threadMessage.trim() || `Attached ${attachments.length} file(s)`,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // Update ticket last_message_at
      await supabase.from('support_tickets')
        .update({ last_message_at: new Date().toISOString(), status: 'awaiting_support' as any })
        .eq('id', activeTicketId);

      setThreadMessage('');
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: ['onboarding-support-messages', activeTicketId] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-support-tickets', clientId] });
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    if (view === 'thread' || view === 'new') {
      setView('list');
      setActiveTicketId(null);
    }
  };

  const activeTicket = tickets.find((t: any) => t.id === activeTicketId);
  const isResolved = activeTicket && ['resolved', 'closed'].includes(activeTicket.status);
  const openTickets = tickets.filter((t: any) => !['resolved', 'closed'].includes(t.status));
  const closedTickets = tickets.filter((t: any) => ['resolved', 'closed'].includes(t.status));

  const resolving = !clientId && !!userId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(calc(100vw-1.5rem),56rem)] max-w-none h-[min(88vh,52rem)] max-h-[calc(100vh-1.5rem)] min-h-0 !flex !flex-col border-border/60 bg-background/95 p-0 gap-0 overflow-hidden rounded-[2rem] shadow-2xl backdrop-blur-xl">
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-accent/10 via-accent/5 to-transparent pointer-events-none" />

        {/* Header */}
        <div className="relative flex shrink-0 items-start gap-4 border-b border-border/60 px-5 py-4 pr-12 sm:px-6 sm:py-5 sm:pr-14">
          {view !== 'list' && (
            <button
              onClick={handleBack}
              className="mt-0.5 h-10 w-10 rounded-2xl border border-border/60 bg-secondary/30 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}

          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-secondary/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground mb-3">
              <HelpCircle className="w-3.5 h-3.5 text-accent" />
              Applicant support
            </div>

            <h2 className="text-2xl font-black tracking-tight text-foreground">
              {view === 'list' ? 'Help & Support' :
               view === 'new' ? 'Create a new ticket' :
               activeTicket?.subject || 'Conversation'}
            </h2>

            {view === 'new' && (
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">
                Ask a question, request help with verification, or send documents to the team handling your application.
              </p>
            )}

            {view === 'thread' && activeTicket && (
              <p className={cn(
                'text-[11px] font-bold uppercase tracking-[0.18em] mt-2',
                activeTicket.status === 'awaiting_client' ? 'text-[hsl(var(--warning))]' :
                activeTicket.status === 'resolved' ? 'text-[hsl(var(--success))]' :
                'text-accent'
              )}>
                {formatLabel(activeTicket.status)}
              </p>
            )}
          </div>

          {view === 'list' && (
            <Button size="sm" className="gap-1.5 rounded-2xl text-xs font-bold h-10 px-4 shadow-lg" onClick={() => setView('new')}>
              <Plus className="w-3.5 h-3.5" /> New Ticket
            </Button>
          )}
        </div>

        {/* List View */}
        {view === 'list' && (
          <ScrollArea className="flex-1 min-h-0">
            {resolving || ticketsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-5 h-5 text-accent animate-spin" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="py-20 text-center px-8">
                <div className="w-16 h-16 rounded-[1.25rem] border border-border/60 bg-secondary/30 flex items-center justify-center mx-auto mb-5 shadow-lg">
                  <HelpCircle className="w-8 h-8 text-accent/70" />
                </div>
                <p className="text-lg font-bold text-foreground mb-1.5">No tickets yet</p>
                <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">Open a support conversation and our team will reply directly inside your application flow.</p>
                <Button size="sm" className="rounded-2xl gap-1.5 text-xs px-4 h-10" onClick={() => setView('new')}>
                  <Plus className="w-3.5 h-3.5" /> Create Ticket
                </Button>
              </div>
            ) : (
              <div>
                {openTickets.length > 0 && (
                  <>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.24em] px-6 pt-5 pb-2">
                      Open ({openTickets.length})
                    </p>
                    <div className="divide-y divide-border/40">
                      {openTickets.map((ticket: any) => (
                        <TicketRow key={ticket.id} ticket={ticket} onClick={() => {
                          setActiveTicketId(ticket.id);
                          setView('thread');
                        }} />
                      ))}
                    </div>
                  </>
                )}
                {closedTickets.length > 0 && (
                  <>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.24em] px-6 pt-5 pb-2">
                      Resolved ({closedTickets.length})
                    </p>
                    <div className="divide-y divide-border/40">
                      {closedTickets.map((ticket: any) => (
                        <TicketRow key={ticket.id} ticket={ticket} onClick={() => {
                          setActiveTicketId(ticket.id);
                          setView('thread');
                        }} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </ScrollArea>
        )}

        {/* New Ticket View */}
        {view === 'new' && (
          <div className="flex-1 min-h-0 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[1.75rem] border border-border/60 bg-secondary/20 shadow-[0_20px_60px_-30px_hsl(var(--foreground)/0.35)]">
              <ScrollArea className="flex-1 min-h-0">
                <div className="grid gap-5 p-4 sm:p-5 md:p-6">
                  <div className="space-y-2.5">
                    <label className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Topic</label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {ONBOARDING_TOPICS.map(t => (
                        <button
                          key={t.value}
                          onClick={() => setNewTopic(t.value)}
                          className={cn(
                            'min-h-[72px] rounded-2xl border px-4 py-3 text-left transition-all duration-200',
                            newTopic === t.value
                              ? 'border-accent/70 bg-accent/10 shadow-[0_12px_30px_-20px_hsl(var(--accent)/0.9)]'
                              : 'border-border/50 bg-background/50 hover:border-accent/30 hover:bg-secondary/40'
                          )}
                        >
                          <span className={cn(
                            'block text-sm font-semibold',
                            newTopic === t.value ? 'text-foreground' : 'text-muted-foreground'
                          )}>
                            {t.label}
                          </span>
                          <span className="mt-1 block text-[11px] leading-relaxed text-muted-foreground">
                            {t.value === 'onboarding_query' ? 'Questions about your application status or next steps.' :
                             t.value === 'document_help' ? 'Get help with uploads, requested files, or accepted formats.' :
                             t.value === 'verification_issue' ? 'Report issues during identity or stakeholder verification.' :
                             'Anything else you need help with from the team.'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-[0.9fr_1.1fr] md:items-start">
                    <div className="space-y-2.5">
                      <label className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Subject</label>
                      <Input
                        placeholder="Brief summary of your question..."
                        value={newSubject}
                        onChange={e => setNewSubject(e.target.value)}
                        className="h-12 rounded-2xl border-border/60 bg-background/70 px-4 text-sm shadow-none focus-visible:ring-1 sm:text-base"
                      />
                    </div>

                    <div className="space-y-2.5">
                      <label className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Message</label>
                      <Textarea
                        placeholder="Describe your question or issue in detail..."
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        rows={4}
                        className="min-h-[140px] resize-none rounded-2xl border-border/60 bg-background/70 px-4 py-3 text-sm shadow-none focus-visible:ring-1 sm:min-h-[152px] sm:text-base"
                      />
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <div className="shrink-0 border-t border-border/50 bg-secondary/20 px-4 py-4 sm:px-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <p className="max-w-md text-xs text-muted-foreground">
                    Your message goes straight to support and stays attached to this application for follow-up replies and document requests.
                  </p>

                  <Button
                    onClick={handleCreateTicket}
                    disabled={creating || !newSubject.trim() || !newMessage.trim()}
                    className="h-12 w-full gap-2.5 rounded-[1.25rem] px-6 text-sm font-black tracking-tight shadow-[0_18px_40px_-20px_hsl(var(--accent)/1)] sm:w-auto sm:text-base"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Submit Ticket
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Thread View */}
        {view === 'thread' && (
          <>
            <ScrollArea className="flex-1 min-h-0">
              <div className="px-4 py-4 space-y-3">
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-5 h-5 text-accent animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-center text-xs text-muted-foreground py-8">No messages yet</p>
                ) : (
                  messages.map((msg: any, i: number) => {
                    const isClient = msg.sender_type === 'client';
                    const isSystem = msg.sender_type === 'system';

                    if (isSystem) {
                      return (
                        <div key={msg.id || i} className="flex justify-center">
                          <div className="text-[10px] text-muted-foreground bg-secondary/40 px-3 py-1 rounded-full">
                            {msg.content}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={msg.id || i} className={cn("flex gap-2", isClient ? "justify-end" : "justify-start")}>
                        {!isClient && (
                          <div className="w-7 h-7 rounded-full bg-accent/15 flex items-center justify-center shrink-0 mt-1">
                            <Headphones className="w-3.5 h-3.5 text-accent" />
                          </div>
                        )}
                        <div className={cn(
                          "max-w-[80%] rounded-2xl px-3.5 py-2.5",
                          isClient
                            ? "bg-accent text-accent-foreground rounded-br-md"
                            : "bg-card border border-border rounded-bl-md"
                        )}>
                          <p className={cn("text-[9px] font-bold uppercase tracking-wider mb-0.5",
                            isClient ? "text-accent-foreground/70" : "text-accent"
                          )}>
                            {isClient ? 'You' : (msg.sender_name || 'Support')}
                          </p>

                          {msg.is_document_request && (
                            <div className={cn(
                              "mb-2 p-2 rounded-lg border text-xs",
                              isClient ? "bg-accent-foreground/10 border-accent-foreground/20" : "bg-[hsl(var(--warning)/0.05)] border-[hsl(var(--warning)/0.2)]"
                            )}>
                              <div className="flex items-center gap-1 mb-0.5">
                                <FileText className="w-3 h-3" />
                                <span className="font-bold text-[10px]">Document Request</span>
                              </div>
                              <p className="text-[11px]">{msg.document_request_type}</p>
                            </div>
                          )}

                          <p className="text-[13px] whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>

                          {msg.attachments && (msg.attachments as any[]).length > 0 && (
                            <div className="mt-1.5 space-y-1">
                              {(msg.attachments as any[]).map((att: any, j: number) => (
                                <button
                                  key={j}
                                  onClick={async () => {
                                    const { data } = await supabase.storage.from('support-attachments').createSignedUrl(att.file_path, 300);
                                    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
                                  }}
                                  className={cn(
                                    "flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] w-full transition-colors",
                                    isClient ? "bg-accent-foreground/10 hover:bg-accent-foreground/20" : "bg-secondary/60 hover:bg-secondary"
                                  )}
                                >
                                  {att.mime_type?.startsWith('image/') ? <Image className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                  <span className="truncate flex-1 text-left">{att.file_name}</span>
                                  <Download className="w-2.5 h-2.5 opacity-60" />
                                </button>
                              ))}
                            </div>
                          )}

                          <p className={cn("text-[9px] mt-1 opacity-50",
                            isClient ? "text-right" : "text-left"
                          )}>
                            {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        {isClient && (
                          <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-1">
                            <User className="w-3.5 h-3.5 text-foreground" />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            {!isResolved ? (
              <div className="px-4 py-3 border-t border-border shrink-0">
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-secondary/60 text-[11px]">
                        <FileText className="w-3 h-3 text-accent" />
                        <span className="truncate max-w-[100px]">{f.name}</span>
                        <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => { if (e.target.files) setFiles(prev => [...prev, ...Array.from(e.target.files!)]); }}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  />
                  <div className="flex-1">
                    <textarea
                      placeholder="Type your message..."
                      value={threadMessage}
                      onChange={e => setThreadMessage(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                      rows={1}
                      className="w-full py-2 px-3 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent/30"
                      style={{ minHeight: '38px', maxHeight: '100px' }}
                    />
                  </div>
                  <Button
                    onClick={handleSendMessage}
                    disabled={sending || (!threadMessage.trim() && files.length === 0)}
                    size="icon"
                    className="rounded-xl h-9 w-9 shrink-0"
                  >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="px-4 py-3 border-t border-border text-center shrink-0">
                <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--success))]" />
                  Ticket resolved
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TicketRow({ ticket, onClick }: { ticket: any; onClick: () => void }) {
  const isAwaiting = ticket.status === 'awaiting_client';
  const isResolved = ['resolved', 'closed'].includes(ticket.status);

  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/10 transition-colors text-left">
      <div className={cn(
        "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
        isAwaiting ? "bg-[hsl(var(--warning)/0.1)]" :
        isResolved ? "bg-[hsl(var(--success)/0.1)]" :
        "bg-accent/10"
      )}>
        {isAwaiting ? <Clock className="w-4 h-4 text-[hsl(var(--warning))]" /> :
         isResolved ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> :
         <MessageSquare className="w-4 h-4 text-accent" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-foreground truncate">{ticket.subject}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={cn(
            "text-[9px] font-bold uppercase",
            isAwaiting ? "text-[hsl(var(--warning))]" :
            isResolved ? "text-[hsl(var(--success))]" :
            "text-accent"
          )}>
            {formatLabel(ticket.status)}
          </span>
          <span className="text-[10px] text-muted-foreground/60">
            {formatDistanceToNow(new Date(ticket.last_message_at || ticket.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0" />
    </button>
  );
}
